import React, {useState, useCallback, useRef, useEffect} from "react";
import {APIProvider, Map, AdvancedMarker, MapMouseEvent, MapCameraChangedEvent, useMapsLibrary} from "@vis.gl/react-google-maps";
import ModalV2 from "../portal/ModalV2";
import {BlueButton} from "../ui/Buttons";
import {GeocodeResponse} from "../../types/geocode";

function parseAddressComponents(
    components: google.maps.GeocoderAddressComponent[],
    lat: number,
    lng: number,
    formattedAddress: string,
): GeocodeResponse {
    const get = (type: string, nameType: "long_name" | "short_name" = "long_name") => {
        const c = components.find(c => c.types.includes(type));
        return c ? c[nameType] : "";
    };
    const streetNumber = get("street_number");
    const route = get("route");
    const premises = get("premises");
    const subpremise = get("subpremise");
    const addressLine1 = [streetNumber, route].filter(Boolean).join(" ") || premises;
    return {
        countryISO: get("country", "short_name"),
        region: get("administrative_area_level_1"),
        county: get("administrative_area_level_2"),
        town: get("postal_town") || get("locality") || get("administrative_area_level_3"),
        postalCode: get("postal_code"),
        addressLines: [addressLine1, subpremise].filter(Boolean),
        latitude: lat,
        longitude: lng,
        formatted: formattedAddress,
        timezone: "",
    };
}

type MapWithSearchProps = {
    initialPosition: google.maps.LatLngLiteral;
    onPositionChange: (position: google.maps.LatLngLiteral) => void;
    fillAddress: boolean;
    onAddressResolved: (address: GeocodeResponse | null) => void;
};

const MapWithSearch: React.FC<MapWithSearchProps> = ({initialPosition, onPositionChange, fillAddress, onAddressResolved}) => {
    const [selectedPosition, setSelectedPosition] = useState<google.maps.LatLngLiteral>(initialPosition);
    const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(initialPosition);
    const [mapZoom, setMapZoom] = useState(8);

    const places = useMapsLibrary("places");
    const geocoding = useMapsLibrary("geocoding");
    const inputRef = useRef<HTMLInputElement>(null);
    const geocoderRef = useRef<google.maps.Geocoder | null>(null);

    // Refs so autocomplete listener always reads latest values without re-registering
    const fillAddressRef = useRef(fillAddress);
    const onAddressResolvedRef = useRef(onAddressResolved);
    useEffect(() => { fillAddressRef.current = fillAddress; }, [fillAddress]);
    useEffect(() => { onAddressResolvedRef.current = onAddressResolved; }, [onAddressResolved]);

    useEffect(() => {
        if (geocoding) {
            geocoderRef.current = new geocoding.Geocoder();
        }
    }, [geocoding]);

    useEffect(() => {
        setSelectedPosition(initialPosition);
        setMapCenter(initialPosition);
    }, [initialPosition]);

    useEffect(() => {
        if (!places || !inputRef.current) return;

        const autocomplete = new places.Autocomplete(inputRef.current, {
            fields: ["geometry", "name", "formatted_address", "address_components"],
        });

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            if (!place.geometry?.location) return;
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const newPosition = {lat, lng};
            setSelectedPosition(newPosition);
            setMapCenter(newPosition);
            setMapZoom(15);
            onPositionChange(newPosition);
            if (fillAddressRef.current && place.address_components) {
                onAddressResolvedRef.current(parseAddressComponents(
                    place.address_components,
                    lat,
                    lng,
                    place.formatted_address ?? "",
                ));
            } else {
                onAddressResolvedRef.current(null);
            }
        });
    }, [places, onPositionChange]);

    const handleMapClick = useCallback((e: MapMouseEvent) => {
        if (!e.detail.latLng) return;
        const pos = e.detail.latLng;
        setSelectedPosition(pos);
        onPositionChange(pos);
        if (fillAddressRef.current && geocoderRef.current) {
            geocoderRef.current.geocode({location: pos}).then(({results}) => {
                if (results?.[0]) {
                    const r = results[0];
                    onAddressResolvedRef.current(parseAddressComponents(
                        r.address_components,
                        pos.lat,
                        pos.lng,
                        r.formatted_address,
                    ));
                } else {
                    onAddressResolvedRef.current(null);
                }
            }).catch(() => onAddressResolvedRef.current(null));
        } else {
            onAddressResolvedRef.current(null);
        }
    }, [onPositionChange]);

    const handleCameraChanged = useCallback((e: MapCameraChangedEvent) => {
        setMapCenter(e.detail.center);
        setMapZoom(e.detail.zoom);
    }, []);

    return (
        <>
            <div className="absolute top-2 right-2 z-10 w-96">
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search for a location..."
                    className="w-full px-4 py-2 rounded-md shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <Map
                mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
                center={mapCenter}
                zoom={mapZoom}
                onCameraChanged={handleCameraChanged}
                onClick={handleMapClick}
                gestureHandling="greedy"
                disableDefaultUI={false}
                zoomControl={true}
                fullscreenControl={false}
                streetViewControl={false}
                mapTypeControl={true}
                style={{width: "100%", height: "100%"}}
            >
                <AdvancedMarker position={selectedPosition}>
                    <div className="w-8 h-8 bg-red-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                </AdvancedMarker>
            </Map>
        </>
    );
};

type CoordinateSelectorProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    initialLatitude?: number;
    initialLongitude?: number;
    onConfirm: (latitude: number, longitude: number, address?: GeocodeResponse) => void;
};

export const CoordinateSelector: React.FC<CoordinateSelectorProps> = ({
    open,
    setOpen,
    initialLatitude = 51.5074,
    initialLongitude = -0.1278,
    onConfirm,
}) => {
    const googleMapsApiKey: string = process.env.REACT_APP_GOOGLE_MAP_API_KEY!;

    const [selectedPosition, setSelectedPosition] = useState<google.maps.LatLngLiteral>({
        lat: initialLatitude,
        lng: initialLongitude,
    });
    const [fillAddress, setFillAddress] = useState(true);
    const [pendingAddress, setPendingAddress] = useState<GeocodeResponse | null>(null);

    useEffect(() => {
        if (open) {
            setSelectedPosition({lat: initialLatitude, lng: initialLongitude});
            setPendingAddress(null);
        }
    }, [open, initialLatitude, initialLongitude]);

    const handlePositionChange = useCallback((position: google.maps.LatLngLiteral) => {
        setSelectedPosition(position);
    }, []);

    const handleConfirm = useCallback(() => {
        const lat = Number(selectedPosition.lat.toFixed(6));
        const lng = Number(selectedPosition.lng.toFixed(6));
        onConfirm(lat, lng, fillAddress && pendingAddress ? pendingAddress : undefined);
        setOpen(false);
    }, [selectedPosition, onConfirm, setOpen, fillAddress, pendingAddress]);

    const handleCancel = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    return (
        <ModalV2 open={open} setOpen={setOpen}>
            <div className="flex flex-col" style={{width: "100%", height: "80vh"}}>
                <div className="p-4">
                    <h2 className="text-xl font-semibold">Select Location</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Search for a location or click on the map to select coordinates
                    </p>
                </div>

                <div className="flex-1 relative">
                    <APIProvider apiKey={googleMapsApiKey} libraries={["places", "geocoding"]}>
                        <MapWithSearch
                            initialPosition={selectedPosition}
                            onPositionChange={handlePositionChange}
                            fillAddress={fillAddress}
                            onAddressResolved={setPendingAddress}
                        />
                    </APIProvider>
                </div>

                <div className="p-4 bg-gray-50">
                    <div className="mb-3 grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="font-semibold">Latitude: </span>
                            <span>{selectedPosition.lat.toFixed(6)}</span>
                        </div>
                        <div>
                            <span className="font-semibold">Longitude: </span>
                            <span>{selectedPosition.lng.toFixed(6)}</span>
                        </div>
                    </div>

                    <label className="flex items-center gap-2 mb-4 cursor-pointer select-none text-sm">
                        <input
                            type="checkbox"
                            checked={fillAddress}
                            onChange={e => setFillAddress(e.target.checked)}
                            className="w-4 h-4 cursor-pointer"
                        />
                        Fill in address details from selected location
                    </label>

                    <div className="flex gap-4">
                        <BlueButton text="Confirm Location" onClick={handleConfirm}/>
                        <button
                            className="px-4 py-2 border rounded-md hover:bg-gray-100"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </ModalV2>
    );
};
